import { computed, inject, ref, watch } from 'vue'
import { defineStore } from 'pinia'

import { WSInstance, WSResponse } from '@/services/ws/utils/types'
import type { AIChatMessageData } from '../utils/types'
import { AccountTypeSuggestion } from './utils/types'
import { AIType } from '../utils/enums'

import { generateId } from '@/entities/utils/helpers'
import { handleCatchedError } from '@/helpers/common'
import { createSuggestionTimeout } from '../utils/timeout'
import { noSentenceRequest, prepareMessages } from '../utils/helpers'
import { readTypeSuggestion } from './utils/helpers'

import { useUserStore } from '@/store/user'

/**
 * The assistant behind the suggested ownership type, for the half of the
 * ownership step where the user creates an account rather than picking one.
 *
 * Built exactly like useAIAccountSuggestionStore, and separate from it for the
 * same reason the prompts are separate: "which account is this?" and "what kind
 * of account should this be?" are different questions, and an answer to one is
 * not an answer to the other.
 *
 * A suggestion is an aid, never a requirement, so every failure is silent to the
 * user — reported through handleCatchedError so Faro sees it, and never as a
 * toast for something they cannot act on.
 */
export const useAIAccountTypeSuggestionStore = defineStore('ai-account-type-suggestion', () => {
  // INIT
  const userStore = useUserStore()

  const ws = inject<WSInstance>('ws')

  const requestId = ref(`tmp_${generateId()}`)

  const suggestion = ref<AccountTypeSuggestion>()
  const loading = ref(false)

  /**
   * Whether a type has been asked for at all, regardless of the answer. The
   * wizard no longer asks on its own — the user asks by clicking "Ask AI" —
   * so this is what tells that control to stop offering itself once it has
   * done its job.
   */
  const asked = ref(false)

  /** The types the live request was sent with. The answer may name only these. */
  let candidates: string[] = []

  /** What has already been asked, so re-entering the step does not re-ask. */
  let askedFor = ''

  /*
   * A websocket that goes quiet leaves `loading` true, and the wait is drawn on
   * screen — a pulse that never resolves reads as the assistant still thinking
   * about a question nobody is going to answer.
   */
  const timeout = createSuggestionTimeout()

  // GETTERS

  const getSuggestion = computed(() => suggestion.value)

  const isLoading = computed(() => loading.value)

  const hasAsked = computed(() => asked.value)

  // ACTIONS

  const reset = () => {
    requestId.value = `tmp_${generateId()}`
    suggestion.value = undefined
    timeout.stop()
    loading.value = false
    asked.value = false
    candidates = []
    askedFor = ''
  }

  /**
   * Asks which of `accountTypes` a new account should be.
   *
   * Asked with or without a sentence. With one, the words are the request; with
   * none — a wizard opened straight from the Add asset dialog — the request
   * says so, and the prompt answers from what usually owns this kind of record
   * and says in its reason that this is what it did. What must not happen is a
   * convention returned as though it had been read, which is why the two cases
   * are distinguishable to the model rather than blank.
   */
  const suggest = async (params: {
    sentence?: string
    flow: string
    transactionType: string
    accountTypes: string[]
  }) => {
    const { sentence, flow, transactionType, accountTypes } = params

    if (!accountTypes.length) return
    // The websocket is absent when VITE_APP_WS_DISABLED is set.
    if (!ws) return

    const request = sentence?.trim() || noSentenceRequest(flow)

    const key = `${flow}|${transactionType}|${request}|${accountTypes.join(',')}`
    if (key === askedFor) return

    askedFor = key
    asked.value = true
    requestId.value = `tmp_${generateId()}`
    candidates = [...accountTypes]
    suggestion.value = undefined
    loading.value = true

    /*
     * Asking again is allowed after a timeout — the question was never
     * answered, so the key is dropped with the wait.
     */
    timeout.start(() => {
      loading.value = false
      askedFor = ''
      handleCatchedError('Account type suggestion timed out — no answer for the account type')
    })

    const { messages } = prepareMessages(request)

    const messageData: AIChatMessageData = {
      ai_type: AIType.ACCOUNT_TYPE_SUGGESTION,
      messages,
      dynamic_context: {
        flow,
        transaction_type: transactionType,
        // Quoted the way every other prompt in this app quotes a list of types.
        account_types: JSON.stringify(accountTypes),
      },
    }

    ws.send('ai', 'chat', requestId.value, messageData)
  }

  watch(
    () => userStore.isAuthenticated,
    value => {
      if (!value) return
      ws?.subscribe(
        'ai',
        'chat',
        (message: WSResponse<string>, responseId: string) => {
          // Every assistant shares this channel; the request id is what says
          // the answer is ours.
          if (responseId !== requestId.value) return

          try {
            if (message.errors || !message.data) {
              handleCatchedError(
                `Account type suggestion failed: ${message.errors?.join(', ') ?? 'empty answer'}`,
              )
              return
            }

            const { suggestion: read, error } = readTypeSuggestion(message.data, candidates)

            if (error) {
              handleCatchedError(error)
              return
            }

            // Absent when the request did not say, which is not an error.
            if (read) suggestion.value = read
          } catch (e) {
            handleCatchedError(e as string)
          } finally {
            timeout.stop()
            loading.value = false
          }
        },
      )
    },
    { immediate: true },
  )

  return {
    isLoading,
    hasAsked,

    getSuggestion,

    suggest,
    reset,
  }
})
